import liviaLogo from './assets/livia-logo.png';
import type { BrandConfig } from './types';

/**
 * Neutral default brand — a true no-op override (every field left unset).
 *
 * MUST be returned whenever the requested `app_id` is missing, malformed,
 * or not present in `BRANDING_MAP`. This is the fail-closed path required by
 * the "Per-App Branding Resolution" spec requirement: unknown apps must
 * render exactly as Logto's stock experience already does today, with zero
 * LivIA (or any other brand) leakage.
 */
export const DEFAULT_BRAND: BrandConfig = {
  brandId: 'default',
};

/**
 * LivIA pilot brand — shared by both `finbot-web` and `livia-mobile`
 * app_ids (one product, two client surfaces).
 */
const LIVIA_BRAND: BrandConfig = {
  brandId: 'livia',
  name: 'LivIA',
  headline: 'Bienvenido a LivIA — tu asistente personal',
  logoUrl: liviaLogo,
  primaryColor: '#006A6B',
};

/**
 * Static `app_id -> BrandConfig` allowlist.
 *
 * Deliberately a `Map`, not a plain object: a plain object keyed by
 * attacker-controlled strings is vulnerable to prototype-chain lookups
 * (`"__proto__"`, `"constructor"`, `"toString"`, ...) returning an
 * unintended value instead of `undefined`. `Map#get` has no prototype
 * chain to walk, so any key that isn't one of the two explicit entries
 * below reliably misses.
 */
export const BRANDING_MAP: ReadonlyMap<string, BrandConfig> = new Map<string, BrandConfig>([
  ['xjmtc6uqx7ivr7cikr65q', LIVIA_BRAND], // App: finbot-web
  ['lkr794uh2sg96oqfjkbtu', LIVIA_BRAND], // App: livia-mobile
]);

/**
 * Resolves the brand to render for a given `app_id`.
 *
 * Fail-closed allowlist lookup: `appId` is ONLY ever used as a `Map` lookup
 * key. It is never concatenated into markup, passed to `innerHTML`, logged
 * as an error, or evaluated. Any value that is not a non-empty string
 * exactly matching a `BRANDING_MAP` key — including `undefined`, `null`,
 * empty string, or a crafted script/HTML payload — resolves to
 * `DEFAULT_BRAND`.
 */
export const resolveBranding = (appId?: unknown): BrandConfig => {
  if (typeof appId !== 'string' || appId.length === 0) {
    return DEFAULT_BRAND;
  }

  return BRANDING_MAP.get(appId) ?? DEFAULT_BRAND;
};
